import { defineComponent, h, markRaw } from 'vue';
import { QBtn, QMenu, QList, QItem, QItemSection, QItemLabel, QIcon, QInput, QSpace, QSeparator, QTooltip } from 'quasar';
import utils from '../../Shared/utils.jsx';
import { normalizeIconName } from '../../Shared/buttonAppearance.js';
import careHelpfulFunctions from '../careHelpfulFunctions.jsx';
import methods from '../../Shared/methods';
import BaseComponent from './BaseComponentMixin.jsx';
import "./css/RealTimeGrid.css";
import { AgGridVue } from 'ag-grid-vue3';
//import 'ag-grid-enterprise'; // Required for SSRM
import _ from 'lodash'; // For debounce
import { mergeRows, matchesQuickSearch, compareRaw, DEFAULT_SOURCE_FIELD } from './realTimeMerge.js';

import TranslationContainer from './TranslationContainer.jsx';

const CellButtons = defineComponent({
    components: {
        QBtn, QMenu, QList, QItem, QItemSection, QItemLabel, QIcon, QSeparator, QTooltip
    },
    data: () => ({
        type: 'CellButtons',
        rowData: null,
        c_context: null,
        condition_expn: null,
    }),
    created() {
        this.rowData = this.params.data;
        this.c_context = this.params.customdata.context;
        this.condition_expn = this.params.customdata.ColumnDef.condition_expn;
        this._conditionWatches = [];

        const rowModelName = this.params.customdata.ColumnDef.RowModelName || 'row';

        for (let btn of this.params.customdata.ColumnDef.CommandButtons) {
            if (btn.Condition && !btn.$$Condition) {
                btn.$$Condition = utils.compileExpression(this, btn.Condition, rowModelName);
            }

            if (btn.Icon && !btn.$$Icon)
                btn.$$Icon = utils.compile(this, btn.Icon, false, rowModelName);

            if (btn.Color && !btn.$$Color)
                btn.$$Color = utils.compile(this, btn.Color, false, rowModelName);

            if (btn.Tooltip && !btn.$$Tooltip)
                btn.$$Tooltip = utils.compile(this, btn.Tooltip, false, rowModelName);

            if (btn.$$Condition) {
                // Keep dispose handles on this cell instance — not on the shared CommandButtons
                // object — so redrawRows remounts cannot dispose a newer cell's watch.
                this._conditionWatches.push(
                    this.$watch(
                        function () {
                            if (btn.$$Icon)
                                utils.evaluate(btn.$$Icon, this.c_context, false, null, false, this.rowData);
                            if (btn.$$Color)
                                utils.evaluate(btn.$$Color, this.c_context, false, null, false, this.rowData);
                            if (btn.$$Tooltip)
                                utils.evaluate(btn.$$Tooltip, this.c_context, false, null, false, this.rowData);

                            return utils.evaluate(btn.$$Condition, this.c_context, false, null, false, this.rowData);
                        },
                        function (val) {
                            //console.log(`Redrawing RealTimeGridAg cell due to a change in the condition: ${btn.$$Condition.code}; value: ${val}`);
                            setTimeout(() => this.params.api.redrawRows(), 0);
                            
                        }
                    )
                );
            }
        }
    },
    unmounted() {
        for (const dispose of this._conditionWatches || []) {
            if (typeof dispose === 'function')
                dispose();
        }
        this._conditionWatches = [];
    },
    methods: {
        ...methods,
        rowMenuClick(e, data, btn, m) {
            e.stopPropagation();
            utils.executeAndCompileAllActions(m.Actions, { RowIndex: -1, Data: this.rowData }, this.c_context);
        },
        menuOpenClose(state, data, btn, m) {
            if (btn.OnMenuOpen)
                utils.executeAndCompileAllActions(btn.OnMenuOpen, { RowIndex: -1, Data: this.rowData, State: state }, this.c_context);
        },
        rowCommandClick(e, data, btn) {
            e.stopPropagation();
            utils.executeAndCompileAllActions(btn.Actions, { RowIndex: -1, Data: this.rowData }, this.c_context);
        },
        refresh(e) {
            for (let item in e.data) {
                this.rowData[item] = e.data[item];
            }
        },
    },
    render() {
        const data = this.rowData;
        const elements = [];
        if (this.condition_expn) {
            try {
                if (!utils.evaluate(this.condition_expn, this.c_context, false, null, false, data))
                    return null;
            }
            catch (ex) {
                console.warn(`Could not evaluate: ${this.condition_expn.code} with data:${JSON.stringify(data)} - ${ex.message}`);
                return null;
            }
        }
        //for (let btn of this.params.customdata.ColumnDef.CommandButtons) {
        for (let i = 0; i < this.params.customdata.ColumnDef.CommandButtons.length; i++) {
            const btn = this.params.customdata.ColumnDef.CommandButtons[i];

            if (!utils.document_cache.hasAccess('real-time grid command button', btn.AccessPermission))
                continue;

            if (btn.$$Condition && !utils.evaluate(btn.$$Condition, this.c_context, false, null, false, data))
                continue;

            let color = btn.$$Color ? utils.evaluate(btn.$$Color, this.c_context, false, null, false, data) : undefined;
            let icon = btn.$$Icon ? normalizeIconName(utils.evaluate(btn.$$Icon, this.c_context, false, null, false, data)) : undefined;

            let tooltip;
            if (btn.$$Tooltip) {
                const tooltipContent = utils.evaluate(btn.$$Tooltip, this.c_context, false, null, false, data);
                if (tooltipContent) {
                    tooltip = (
                        <QTooltip transition-show="scale" transition-hide="scale" anchor="center right" self="center left">
                            {utils.renderTooltipContent(tooltipContent)}
                        </QTooltip>
                    );
                }
            }

            const rowModelName = this.params.customdata.ColumnDef.RowModelName || 'row';

            let b;
            if (btn.Menu && btn.Menu.length > 0) {
                const items = [];
                for (let j = 0; j < btn.Menu.length; j++) {
                    const m = btn.Menu[j];
                    if (m.Separator)
                        items.push(<QSeparator key={`sep-${j}`}></QSeparator>);
                    else {
                        if (!utils.document_cache.hasAccess('real-time grid command button menu', m.AccessPermission))
                            continue;

                        if (m.EnabledExpression && !m.$$enabledexpn)
                            m.$$enabledexpn = utils.compileExpression(this, m.EnabledExpression, rowModelName);

                        if (m.Icon && !m.$$iconexpn)
                            m.$$iconexpn = utils.compile(this, m.Icon, rowModelName);

                        if (m.Icon && m.IconColor && !m.$$iconcolorexpn)
                            m.$$iconcolorexpn = utils.compile(this, m.IconColor, rowModelName);

                        let menuicon;
                        if (m.$$iconexpn) {
                            let iconcolor = m.$$iconcolorexpn ? utils.evaluate(m.$$iconcolorexpn, this.c_context, false, null, false, data) : undefined;
                            menuicon = (
                                <QItemSection side>
                                    <QIcon style={{ color: iconcolor }} size="sm" name={normalizeIconName(utils.evaluate(m.$$iconexpn, this.c_context, false, null, false, data))}></QIcon>
                                </QItemSection>
                            );
                        }

                        items.push(
                            <QItem
                                dense
                                clickable
                                key={j}
                                disable={(m.$$enabledexpn && !utils.evaluate(m.$$enabledexpn, this.c_context, false, null, false, data)) ? true : false}
                                onClick={(e) => this.rowMenuClick(e, data, btn, m)}>
                                {menuicon}
                                <QItemSection>
                                    <QItemLabel>{m.Title}</QItemLabel>
                                </QItemSection>
                            </QItem>
                        );
                    }
                }

                b = (
                    <QBtn key={`cmd-${i}`} flat class="q-mr-xs"
                        style={{ color: color }}
                        icon={icon}
                        v-show={!btn.$$Condition || utils.evaluate(btn.$$Condition, this.c_context, false, null, false, data)}
                        size="sm"
                        dense
                    >
                        {tooltip}
                        <QMenu
                            auto-close
                            onShow={() => this.menuOpenClose(true, data, btn)}
                            onHide={() => this.menuOpenClose(false, data, btn)}
                        >
                            <QList dense>
                                {items}
                            </QList>
                        </QMenu>
                    </QBtn>
                );
            }
            else if (btn.Actions && btn.Actions.length > 0) {
                b = (
                    <QBtn key={`cmd-${i}`} flat class="q-mr-xs"
                        style={{ color: color }}
                        dense
                        icon={icon}
                        size="sm"
                        onClick={(e) => this.rowCommandClick(e, data, btn)}>
                        {tooltip}
                    </QBtn>
                );
            }
            else {
                b = (
                    <QBtn key={`cmd-${i}`} flat class="q-mr-xs"
                        style={{ cursor: 'default', color: color }}
                        unelevated
                        dense
                        icon={icon}
                        size="sm">
                        {tooltip}
                    </QBtn>
                );
            }

            elements.push(b);
        }
        return (<div class="c-RealTimeGrid-button" style="overflow: hidden;">{elements}</div>);
    }
});

export default defineComponent({
    name: 'real-time-grid',
    mixins: [BaseComponent],
    components: {
        TranslationContainer,
        CellButtons,
        AgGridVue,
        QBtn, QMenu, QList, QItem, QItemSection, QItemLabel, QIcon, QInput, QSpace
    },
    data: function () {
        return {
            id: null,
            gridName: null,
            totalRows: 0,
            rowdata: [],
            loading: false,
            columnDefs: [],
            gridOptions: null,
            gridApi: null,
            dense: false,
            searchText: "",
            viewname: 'UserList~!!~null',
            sortCol: 'StartTime',
            descending: false,
            start: 0,
            paginationPageSize: 10, // Replaces length for SSRM
            defaultRowsPerPage: 5,
            selected_items: [],
            selectableKey: "id",
            column_visibility: null,
            menu_is_open: false,
            lastRequestParams: null,
            isSubscribed: false,
            rtretry$: null,
            retryCount$: 0,
            currentPage: 1,
            resizeObserver: null,
            datasource: null,
            serverDataSource: null,
            autoSizeStrategy: {
                type: 'fitCellContents',
                defaultMinWidth: 100,
            },

            rteventsource_func: null,
            rteventsources_func: null,
            merged: false,
            mergeNotice: '',
            listNameExpn: null,
            filtersexpn: null,
            selectableexpn: null,
        };
    },
    async created() {
        this.gridName = `RealTimeGrid_${this.controlData.Name || 'Unnamed'}`;
        const settings = this.System.LocalStorage(this.gridName + '.ColumnSettings');
        if (settings) {
            this.column_visibility = JSON.parse(settings);
        } else if (this.controlData.ColumnDefs && this.controlData.ColumnDefs.length) {
            this.column_visibility = careHelpfulFunctions.arrayToObject(this.controlData.ColumnDefs, a => a.Field, b => b.Visible);
        } else {
            this.column_visibility = {};
        }

        const options = this.System.LocalStorage(this.gridName + '.Options');
        if (options) {
            const parsed = JSON.parse(options);
            this.gridOptions = {
                ...parsed,
                rowModelType: 'serverSide',
                serverSideDatasource: this.createServerSideDatasource(),
                pagination: true,
                paginationPageSize: this.paginationPageSize,
                animateRows: true,
                paginationAutoPageSize: true,
                getRowId: (params) => {
                    return params.data._id.toString();
                },
                enableCellTextSelection: true,
            };
        } else {
            this.gridOptions = {
                rowModelType: 'serverSide',
                serverSideDatasource: this.createServerSideDatasource(),
                pagination: true,
                paginationPageSize: this.paginationPageSize,
                animateRows: true,
                paginationAutoPageSize: true,
                getRowId: (params) => {
                    return params.data._id.toString();
                },
                enableCellTextSelection: true,
            };
        }

        if (this.controlData.RTEventSource)
            this.rteventsource_func = utils.compileExpression(this, this.controlData.RTEventSource);

        // Merged mode: RTEventSources lists several sources whose rows share one grid. The grid then
        // holds every row itself (client side row model) and sorts, filters and pages in the browser.
        if (this.controlData.RTEventSources) {
            this.merged = true;
            this.rteventsources_func = utils.compileExpression(this, this.controlData.RTEventSources);
            this.merge$ = { entries: new Map(), seq: 0, timer: null };
            const { serverSideDatasource, ...rest } = this.gridOptions;
            this.gridOptions = {
                ...rest,
                rowModelType: 'clientSide',
                getRowId: (params) => String(params.data._id),
            };
        }

        if (this.controlData.ListSource)
            this.listNameExpn = utils.compileObject(this, this.controlData.ListSource);

        if (this.controlData.Filters)
            this.filtersexpn = utils.compileObject(this, this.controlData.Filters);

        if (this.controlData.SelectOptions && this.controlData.SelectOptions.Enable && this.controlData.SelectOptions.SelectableExpression)
            this.selectableexpn = utils.compileExpression(this, this.controlData.SelectOptions.SelectableExpression, 'row');

        this.dense = !!this.controlData.Dense;

        if (this.controlData.SelectOptions && this.controlData.SelectOptions.Enable && this.controlData.SelectOptions.SelectableKey)
            this.selectableKey = this.controlData.SelectOptions.SelectableKey;

        if (this.controlData.DefaultRowsPerPage)
            this.defaultRowsPerPage = this.controlData.DefaultRowsPerPage;

        this.setupColumnDefs();
    },
    mounted() {
        //this.updateVisibleRows();
        //this.setupResizeObserver();
        //window.addEventListener('resize', this.handleResize);
    },
    beforeDestroy() {
        //if (this.resizeObserver) {
        //    this.resizeObserver.disconnect();
        //}
        //window.removeEventListener('resize', this.handleResize);
    },
    unmounted() {
        if (this.watch_date_value$) this.watch_date_value$();
        if (this.datawatch$) this.datawatch$();
        if (this.filters_watch$) this.filters_watch$();
        if (this.listname_watch$) this.listname_watch$();
        if (this.rtactive_watch$) this.rtactive_watch$();
        if (this.rtretry$) clearTimeout(this.rtretry$);
        if (this.viewname && this.id && this.rtEvents) this.rtEvents.RemoveNamedListForRealTime(this.viewname, this.id);
        if (this.merge_watch$) this.merge_watch$();
        this.mergeTeardown();
    },
    computed: {
        filters: function () {
            return this.filtersexpn ? utils.evaluateObject(this.filtersexpn, this) : null;
        },
        listName: function () {
            return this.listNameExpn ? utils.evaluateObject(this.listNameExpn, this) : null;
        },
        serverProperties: function () {
            const columns = this.generateHeaders().filter(a => a.field && a.field != '_menu_' && !(this.merged && a.field == this.mergeSourceField)).map(a => a.field);
            let serverProperties = columns;
            if (this.controlData.AdditionalProperties) {
                const additionalProperties = this.controlData.AdditionalProperties.filter(a => a).map(b => b.trim());
                serverProperties = _.uniq([...columns, ...additionalProperties]);
            }
            return serverProperties;
        },


        rtEvents: function () {
            return this.rteventsource_func ? utils.evaluate(this.rteventsource_func, this) : null;
        },
        // Merged mode: [{ Name, Source (an RT event source), Center (identity for rows without one) }]
        mergeSources: function () {
            if (!this.rteventsources_func)
                return [];
            const list = utils.evaluate(this.rteventsources_func, this);
            return Array.isArray(list) ? list.filter(s => s && s.Name) : [];
        },
        mergeSourceField: function () {
            return this.controlData.SourceField || DEFAULT_SOURCE_FIELD;
        },
        mergeMaxRows: function () {
            return Number(this.controlData.MergeMaxRows) || 1000;
        },
        rtViews: function () {
            // View cache of this grid's RT event source. Falls back to the native slot so
            // grids with a missing/unresolved RTEventSource behave exactly as before.
            const src = this.rtEvents || this.GlobalVars.RTData;
            return src ? src.Views : undefined;
        },
        showSelect: function () {
            return this.controlData.SelectOptions.Enable;
        },
        singleSelect: function () {
            return !this.controlData.SelectOptions.MultiSelect;
        },
        agRowSelection: function () {
            if (!this.showSelect) {
                return undefined;
            }

            // Match Vue2 RealTimeGridAg: header select-all only when MultiSelect is false.
            const mode = this.singleSelect ? 'singleRow' : 'multiRow';
            const config = {
                mode,
                headerCheckbox: this.singleSelect,
                enableClickSelection: false,
                isRowSelectable: (rowNode) => {
                    if (!this.controlData.SelectOptions?.Enable) {
                        return false;
                    }

                    if (this.selectableexpn) {
                        return utils.evaluate(this.selectableexpn, this, false, null, false, rowNode.data);
                    }

                    return true;
                },
            };

            // Vue2 headerCheckboxSelectionFilteredOnly → AG Grid 34 selectAll: 'filtered'
            if (mode === 'multiRow') {
                config.selectAll = 'filtered';
            }

            return config;
        },
        SelectedRowsAsArray: {
            get: function () {
                return this.selected_items;
            },
            set: function (value) {
                this.selected_items = value;
            }
        },
        totalPages: function () {
            return Math.ceil(this.totalRows / this.paginationPageSize) || 1;
        },
        canGoPrevious: function () {
            return this.currentPage > 1;
        },
        canGoNext: function () {
            return this.currentPage < this.totalPages;
        },
    },
    methods: {
        preRenderComplete() {
            this.finishRenderHandler(this);

            this.filters_watch$ = this.$watch(
                () => this.filters,
                (newVal, oldVal) => {
                    if (!_.isEqual(newVal, oldVal) && this.gridApi) {
                        this.currentPage = 1;
                        if (this.merged)
                            this.mergeRefetch();
                        else
                            this.gridApi.refreshServerSide({ purge: true });
                    }
                },
                { deep: true }
            );

            this.listname_watch$ = this.$watch(
                () => this.listName,
                (newVal, oldVal) => {
                    if (newVal !== oldVal && this.gridApi) {
                        this.currentPage = 1;
                        if (this.merged)
                            this.mergeRefetch();
                        else
                            this.gridApi.refreshServerSide({ purge: true });
                    }
                },
                { deep: true }
            );

            // The grid subscribes once, when ag-grid is ready. A grid mounted before its
            // event source is streaming - a cross-center source added while the dashboard
            // is open - fails that one attempt and would stay empty until a page reload.
            // Re-run the subscription when the source comes up.
            this.rtactive_watch$ = this.$watch(
                () => {
                    const src = this.rtEvents;
                    return !!(src && src.IsActive);
                },
                (isActive) => {
                    if (this.merged || !isActive || this.isSubscribed || !this.gridApi)
                        return;

                    this.lastRequestParams = null;
                    this.gridApi.refreshServerSide({ purge: true });
                }
            );
        },
        // Merged mode ------------------------------------------------------------------------------
        // Each source keeps its own subscription for the whole list; its pushes update that source's
        // rows and the merged list is rebuilt from all of them.
        mergeSync() {
            const st = this.merge$;
            if (!st || !this.gridApi)
                return;

            const wanted = this.mergeSources;
            for (const [name, e] of st.entries) {
                if (!wanted.some(s => s.Name === name)) {
                    this.mergeDrop(e);
                    st.entries.delete(name);
                }
            }

            for (const s of wanted) {
                let e = st.entries.get(s.Name);
                if (!e) {
                    e = { name: s.Name, id: null, viewname: null, rows: [], total: 0, stamp: 0, subscribed: false, pending: false, watch: null, source: null };
                    st.entries.set(s.Name, e);
                }
                if (e.source && e.source !== s.Source) {
                    this.mergeDrop(e);
                    e.rows = [];
                    e.total = 0;
                    e.id = null;
                    e.viewname = null;
                    e.subscribed = false;
                }
                e.source = s.Source;
                e.center = s.Center || s.Name;

                if (!e.source || !e.source.IsActive)
                    e.subscribed = false;
                else if (!e.subscribed)
                    this.mergeSubscribe(e);
            }

            this.mergeRebuild();
        },
        async mergeSubscribe(e) {
            if (e.pending)
                return;
            e.pending = true;
            const source = e.source;
            try {
                const filter = { ...(this.filters || {}) };
                delete filter._any_; // quick search runs in the browser, over the merged rows
                const res = await source.GetNamedListForRealTime(this.listName, this.sortCol, this.descending, 0, this.mergeMaxRows, this.serverProperties, filter, e.id);
                if (!this.merge$ || this.merge$.entries.get(e.name) !== e || e.source !== source)
                    return;

                if (res && res.Response) {
                    e.id = res.ID;
                    e.viewname = res.ViewHash;
                    source.Views[e.viewname] = { Refreshing: true, Total: res.Response.Total, Data: res.Response.Data };
                    e.rows = res.Response.Data || [];
                    e.total = res.Response.Total || 0;
                    e.stamp = ++this.merge$.seq;
                    e.subscribed = true;

                    if (e.watch)
                        e.watch();
                    e.watch = this.$watch(
                        () => source.Views && source.Views[e.viewname],
                        (v) => {
                            if (!v || v.Refreshing || !this.merge$)
                                return;
                            e.rows = v.Data || [];
                            e.total = v.Total || 0;
                            e.stamp = ++this.merge$.seq;
                            this.mergeRebuildSoon();
                        },
                        { deep: true }
                    );
                }
            } catch (error) {
                console.error(`RealTimeGrid merged source ${e.name} failed:`, error);
                e.subscribed = false;
            } finally {
                e.pending = false;
                this.mergeRebuildSoon();
            }
        },
        mergeRefetch() {
            if (!this.merge$)
                return;
            for (const e of this.merge$.entries.values())
                e.subscribed = false;
            this.mergeSync();
        },
        mergeDrop(e) {
            if (e.watch)
                e.watch();
            e.watch = null;
            try {
                if (e.viewname && e.id && e.source)
                    e.source.RemoveNamedListForRealTime(e.viewname, e.id);
            } catch (err) { /* source already gone */ }
        },
        mergeTeardown() {
            const st = this.merge$;
            if (!st)
                return;
            if (st.timer)
                clearTimeout(st.timer);
            for (const e of st.entries.values())
                this.mergeDrop(e);
            st.entries.clear();
            this.merge$ = null;
        },
        mergeRebuildSoon() {
            const st = this.merge$;
            if (!st || st.timer)
                return;
            st.timer = setTimeout(() => {
                if (!this.merge$)
                    return;
                this.merge$.timer = null;
                this.mergeRebuild();
            }, 50);
        },
        mergeRebuild() {
            const st = this.merge$;
            if (!st || !this.gridApi)
                return;

            const entries = this.mergeSources.map(s => st.entries.get(s.Name)).filter(Boolean);
            let rows = mergeRows(
                entries.map(e => ({ name: e.name, center: e.center, rows: e.rows, stamp: e.stamp })),
                { keyFields: this.controlData.MergeKeyFields || [], listFields: this.controlData.MergeListFields || [], sourceField: this.mergeSourceField }
            );
            if (this.searchText)
                rows = rows.filter(r => matchesQuickSearch(r, this.searchText));

            // The order a single-source grid gets from its server, until a column sort takes over.
            rows.sort((a, b) => compareRaw(a[this.sortCol], b[this.sortCol], this.descending) * (this.descending ? -1 : 1));

            this.rowdata = rows;
            this.totalRows = rows.length;
            this.mergeNotice = entries
                .filter(e => e.total > (e.rows || []).length)
                .map(e => `${e.name}: ${this.Translate('showing')} ${(e.rows || []).length} / ${e.total}`)
                .join('; ');

            this.gridApi.setGridOption('rowData', rows);
            if (rows.length == 0)
                this.gridApi.showNoRowsOverlay();
            else
                this.gridApi.hideOverlay();

            this.gridApi.refreshCells({
                columns: this.columnDefs.filter(cd => cd.cellRenderer === 'CellButtons').map(cd => cd.field),
                force: true,
            });
        },
        scheduleSubscribeRetry() {
            // The IsActive watcher above cannot help when the event source itself was
            // still undefined at mount, because no dependency on IsActive is registered
            // in that case. Back off up to ~30s for a bounded number of attempts.
            if (this.rtretry$ || this.retryCount$ >= 10)
                return;

            const delay = Math.min(1000 * Math.pow(2, this.retryCount$++), 30000);
            this.rtretry$ = setTimeout(() => {
                this.rtretry$ = null;

                const src = this.rtEvents;
                if (!this.isSubscribed && src && src.IsActive && this.gridApi) {
                    this.lastRequestParams = null;
                    this.gridApi.refreshServerSide({ purge: true });
                }
            }, delay);
        },
        addDataWatch() {
            if (!this.rtViews[this.viewname])
                this.rtViews[this.viewname] = { Total: 0, Data: [] };

            // Dispose any existing watch
            if (this.datawatch$)
                this.datawatch$();

            this.datawatch$ = this.$watch(
                () => {
                    const views = this.rtViews;
                    return views && views[this.viewname];
                },
                (newv, oldv) => {
                    if (newv && newv.Refreshing) {
                        //utils.log(`RTData.Views['${this.viewname}'] changed but was refreshing from server side, so ignoring`);
                        return;
                    }

                    if (newv && this.gridApi) {
                        this.rowdata = newv.Data;
                        this.totalRows = newv.Total;

                        //utils.log(`RTData.Views['${this.viewname}'] changed; newv:${JSON.stringify(newv.Data)}`);
                        //utils.log(`RTData.Views['${this.viewname}'] changed; oldv:${JSON.stringify(oldv ? oldv.Data : [])}`);

                        if (this.totalRows == 0)
                            this.gridApi.showNoRowsOverlay();
                        else
                            this.gridApi.hideOverlay();

                        //this.rowdata.forEach((row, index) => row._id = index);

                        // Identify added, updated, and removed rows
                        const oldData = oldv && oldv.Data ? oldv.Data : [];
                        const newData = newv.Data || [];

                        // Added rows: Present in newData but not in oldData
                        const addedRows = newData.filter(
                            newRow => !oldData.some(oldRow => oldRow._id === newRow._id)
                        );

                        // Removed rows: Present in oldData but not in newData
                        const removedRows = oldData.filter(
                            oldRow => !newData.some(newRow => newRow._id === oldRow._id)
                        );

                        // Updated rows: Present in both, but data has changed
                        const updatedRows = newData.filter(newRow =>
                            oldData.some(oldRow =>
                                oldRow._id === newRow._id &&
                                !_.isEqual(oldRow, newRow)
                            )
                        );

                        // Apply transactions
                        if (removedRows.length > 0 || updatedRows.length > 0 || addedRows.length > 0) {
                            //utils.log(`RTData.Views['${this.viewname}'] changed; removedRows:${JSON.stringify(removedRows)}`);
                            //utils.log(`RTData.Views['${this.viewname}'] changed; updatedRows:${JSON.stringify(updatedRows)}`);
                            //utils.log(`RTData.Views['${this.viewname}'] changed; addedRows:${JSON.stringify(addedRows)}`);
                            this.gridApi.applyServerSideTransaction({
                                remove: removedRows,
                                update: updatedRows,
                                add: addedRows,
                            });
                        }
                        
                        this.gridApi.setRowCount(this.totalRows);

                        // Refresh cells to update custom renderers (CellButtons)
                        if (addedRows.length > 0 || updatedRows.length > 0) {
                            this.gridApi.refreshCells({
                                columns: this.columnDefs
                                    .filter(cd => cd.cellRenderer === 'CellButtons')
                                    .map(cd => cd.field),
                                force: true,
                            });
                        }
                    }
                },
                { deep: true }
            );
        },
        updateSearchFilter(filter) {
            this.searchText = filter;
            if (!filter && this.filters?._any_) {
                delete this.filters._any_;
            }
            this.currentPage = 1;
            if (this.merged)
                this.mergeRebuild();
            else
                this.gridApi.refreshServerSide({ purge: true });
        },
        createServerSideDatasource() {
            return {
                getRows: async (params) => {
                    const request = params.request;
                    const currentRequestParams = {
                        listName: this.listName,
                        sortCol: request.sortModel.length > 0 ? request.sortModel[0].colId : this.sortCol,
                        descending: request.sortModel.length > 0 ? request.sortModel[0].sort === 'desc' : this.descending,
                        start: request.startRow,
                        length: request.endRow - request.startRow,
                        filter: { ...this.filters, ...(this.searchText ? { "_any_": this.searchText } : {}) },
                    };

                    if (this.isSubscribed && _.isEqual(currentRequestParams, this.lastRequestParams)) {
                        params.success({
                            rowData: this.rowdata,
                            rowCount: this.totalRows
                        });
                        return;
                    }

                    this.loading = true;
                    this.lastRequestParams = currentRequestParams;

                    let filter = this.filters || {};
                    if (this.searchText) {
                        filter["_any_"] = this.searchText;
                    }

                    try {
                        //utils.log(`GetNamedListForRealTime(listName:${this.listName},sortCol:${currentRequestParams.sortCol},descending:${currentRequestParams.descending},startRow:${request.startRow},length:${request.endRow - request.startRow},,filter:${JSON.stringify(filter)},id:${this.id})`);
                        const res = await this.rtEvents.GetNamedListForRealTime(
                            this.listName,
                            currentRequestParams.sortCol,
                            currentRequestParams.descending,
                            request.startRow,
                            request.endRow - request.startRow,
                            this.serverProperties,
                            filter,
                            this.id
                        );

                        if (res && res.Response) {
                            //utils.log(`RTView GetNamedListForRealTime(); id:${this.id}; res.ViewHash:${res.ViewHash}, id:${res.ID}; rawdata:${JSON.stringify(res.Response)}`);

                            const newViewname = res.ViewHash;
                            this.id = res.ID;
                            const rawdata = res.Response;

                            this.rtViews[newViewname] = {
                                Refreshing: true,
                                Total: rawdata.Total,
                                Data: rawdata.Data,
                            };

                            this.rowdata = rawdata.Data;
                            this.totalRows = rawdata.Total;

                            this.rowdata.forEach((row, index) => row._id = index);

                            if (newViewname !== this.viewname && this.viewname) {
                                //this.rtEvents.RemoveNamedListForRealTime(this.viewname, this.id);
                            }
                            this.viewname = newViewname;
                            this.isSubscribed = true;
                            this.retryCount$ = 0;
                            this.addDataWatch();

                            if (rawdata.Total == 0)
                                params.api.showNoRowsOverlay();
                            else
                                params.api.hideOverlay();

                            params.success({
                                rowData: rawdata.Data,
                                rowCount: rawdata.Total
                            });
                        } else {
                            if (!this.rtViews[this.viewname])
                                this.rtViews[this.viewname] = { Refreshing: true, Total: 0, Data: [] };

                            this.rowdata = this.rtViews[this.viewname].Data;
                            this.totalRows = this.rtViews[this.viewname].Total;

                            if (this.totalRows == 0)
                                params.api.showNoRowsOverlay();
                            else
                                params.api.hideOverlay();

                            params.success({
                                rowData: this.rowdata,
                                rowCount: this.totalRows
                            });
                        }
                    } catch (error) {
                        console.error('Error fetching data:', error);
                        // Render an empty grid instead of ag-grid's ERR row - a revoked
                        // or unreachable source should read as "no data", not an error
                        // artifact in the row area.
                        this.rowdata = [];
                        this.totalRows = 0;
                        params.api.showNoRowsOverlay();
                        // Reporting success marks the block loaded, so ag-grid will not
                        // ask again on its own. Clear the subscription state and the
                        // identical-request guard so a later retry can get through.
                        this.isSubscribed = false;
                        this.lastRequestParams = null;
                        this.scheduleSubscribeRetry();
                        params.success({ rowData: [], rowCount: 0 });
                    } finally {
                        this.loading = false;
                    }
                }
            };
        },
        getMenuContent: function () {
            //const h = this.$createElement;
            const menus = [];
            if (this.controlData.ColumnDefs && this.controlData.ColumnDefs.length) {
                for (let c of this.controlData.ColumnDefs.filter(cd => cd.Field)) {
                    menus.push(
                        <QItem key={`${c.Field}`} dense clickable onClick={() => { this.toggleColumnVisibility(c.Field); }}>
                            <QItemSection side>
                                <QIcon name={this.column_visibility[c.Field] ? 'mdi-check' : ''}></QIcon>
                            </QItemSection>
                            <QItemSection>
                                <QItemLabel>
                                    <TranslationContainer context={this} value={c.DisplayName || c.Field} />
                                </QItemLabel>
                            </QItemSection>
                        </QItem>
                    );
                }
            }
            
            return (
                <QBtn flat icon="mdi-menu" dense size="md">
                    <QMenu
                        anchor="bottom right"
                        self="top right"
                        modelValue={this.menu_is_open}
                        onUpdate:modelValue={(state) => this.menu_is_open = state}
                    >
                        <QList dense style="max-height: 600px" class="overflow-auto">
                            {menus}
                        </QList>
                    </QMenu>
                </QBtn>
            );
        },
        getGridHeaderControls: function () {
            //const h = this.$createElement;
            const items = [];
            if (this.controlData.GridHeaderControls && Array.isArray(this.controlData.GridHeaderControls) && this.controlData.GridHeaderControls.length > 0) {
                for (let i = 0; i < this.controlData.GridHeaderControls.length; i++) {
                    const c = this.controlData.GridHeaderControls[i];

                    let DynamicControl = utils.getDynamicComponent(h, c);

                    if (!DynamicControl)
                        DynamicControl = 'default-unknown';

                    items.push(
                        <DynamicControl
                            onFinishedRender={(reference) => this.finishRenderHandler(reference)}
                            type={c.ControlType}
                            name={c.ControlData.Name ? c.ControlData.Name : ''}
                            root={this.root}
                            parentType="HorizontalStack"
                            controlData={c.ControlData}
                            controlURL={c.ControlURL}
                            controlName={c.Name}
                            accessPermission={c.AccessPermission}
                            scopeitems={this.scopeitems}
                            controlscope={this.controlscope}
                            cacheControl={c.CacheControl}
                            controlEvents={c.Events}>
                        </DynamicControl>);
                }
            }
            return items;
        },
        onGridReady(params) {
            this.gridApi = params.api;
            if (this.merged) {
                this.merge_watch$ = this.$watch(
                    () => this.mergeSources.map(s => `${s.Name}:${!!(s.Source && s.Source.IsActive)}`).join('|'),
                    () => this.mergeSync()
                );
                this.mergeSync();
            }
            else
                this.serverDataSource = this.createServerSideDatasource();

            this.watch_date_value$ = this.$watch(function () {
                return utils.helpers.chf.date_value.value;
            }, function (newvalue) {
                this.gridApi.refreshCells();
            });

            // Access the first child div of the component's root element to
            // add a class which will correct a bug when trying to include
            // ag-grid in a dialog. The inner div only has a height: 100%
            // which does not work in a dialog (not sure why). This adds
            // a class that sets display: flex, flex-grow: 1, etc. (2025-06-17 DP)
            const nestedDiv = this.$refs.agGridRef?.$el.children[0];
            if (nestedDiv && nestedDiv.tagName === 'DIV') {
                nestedDiv.classList.add('ag-grid-flex-layout');
            }
        },
        onSortChanged(params) {
            if (!this.merged)
                this.gridApi.refreshServerSide({ purge: true });
        },
        onFirstDataRendered(params) {
            this.gridApi.autoSizeAllColumns();
        },
        toggleColumnVisibility(fieldName) {
            this.column_visibility[fieldName] = !this.column_visibility[fieldName];
            this.System.LocalStorage(this.gridName + '.ColumnSettings', JSON.stringify(this.column_visibility));
            this.setupColumnDefs();

            //this.gridApi.setColumnDefs(this.columnDefs);
            this.gridApi.setColumnsVisible([fieldName], this.column_visibility[fieldName]);
        },
        selectedItemsChanged() {
            const selectedNodes = this.gridApi.getSelectedNodes();
            this.selected_items = selectedNodes.map(node => node.data);
        },
        actionButtons(params, cd) {
            const buttons = [];
            for (let cb of cd.CommandButtons) {
                buttons.push(
                    <CellButton
                        btn={cb}
                        cd={cd}
                        c_context={this}
                        rowData={params.data}
                    />
                );
            }
            return buttons;
        },
        setupColumnDefs() {
            const tempColumnDefs = [];

            if (this.controlData.ColumnDefs) {
                for (let cd of this.controlData.ColumnDefs) {
                    let field_extractor = () => '';
                    if (cd.Field) {
                        field_extractor = cd.field_extractor || new Function('f', `return f.${cd.Field};`);
                    }
                    
                    const isVisible = this.column_visibility.hasOwnProperty(cd.Field) ? this.column_visibility[cd.Field] : cd.Visible !== false;

                    const colDef = {
                        field: cd.Field,
                        headerName: this.Translate(cd.DisplayName || cd.Field || ''),
                        sortable: cd.ActionOnlyColumn ? false : cd.Sortable !== false,
                        hide: !isVisible,
                        resizable: true,
                        suppressHeaderMenuButton: true,
                        cellStyle: { 
                            textAlign: 'left', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'flex-start',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap'
                        }
                    };

                    if (cd.Align) {
                        const justifyMap = { 'start': 'flex-start', 'end': 'flex-end', 'center': 'center', 'left': 'flex-start', 'right': 'flex-end' };
                        
                        colDef.cellStyle = { 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: justifyMap[cd.Align] || cd.Align,
                            textAlign: cd.Align === 'start' ? 'left' : (cd.Align === 'end' ? 'right' : cd.Align),
                            overflow: 'hidden',
                            whiteSpace: 'nowrap'
                        };
                    }

                    if (cd.Field) {
                        if (cd.Condition && !cd.condition_expn)
                            cd.condition_expn = utils.compileExpression(this, cd.Condition, [cd.RowModelName || 'row', cd.ItemModelName || 'item']);
                        if (cd.Value && !cd.value_expn)
                            cd.value_expn = utils.compile(this, cd.Value, false, [cd.RowModelName || 'row', cd.ItemModelName || 'item']);
                        if (cd.CellFilter && !cd.cellfilter_expn) {
                            const filter = `{{ row.${cd.Field} | ${cd.CellFilter} }}`;
                            cd.cellfilter_expn = utils.compile(this, filter, false, ['row', cd.ItemModelName || 'item']);
                        }
                    }
                    //if (cd.Align) {
                    //    colDef.cellStyle = { ...colDef.cellStyle, justifyContent: cd.Align };
                    //}

                    if (cd.ActionOnlyColumn) {
                        colDef.cellRenderer = 'CellButtons';
                        colDef.cellRendererParams = {
                            customdata: {
                                root: this.root,
                                scopeitems: this.scopeitems,
                                controlscope: this.controlscope,
                                context: this,
                                ColumnDef: cd,
                            }
                        };
                    }
                    else {
                        let valueGetter, valueFormatter;

                        if (cd.condition_expn && cd.value_expn) {
                            valueGetter = (params) => utils.evaluate(cd.condition_expn, this, false, null, false, [params.data, field_extractor(params.data)], true)
                                ? utils.evaluate(cd.value_expn, this, false, null, false, [params.data, field_extractor(params.data)], true)
                                : '';
                        } else if (cd.value_expn) {
                            valueGetter = (params) => utils.evaluate(cd.value_expn, this, false, null, false, [params.data, field_extractor(params.data)], true);
                        }

                        if (cd.cellfilter_expn && cd.condition_expn) {
                            valueFormatter = (params) => utils.evaluate(cd.condition_expn, this, false, null, false, [params.data, field_extractor(params.data)], true)
                                ? utils.evaluate(cd.cellfilter_expn, this, false, null, false, [params.data, field_extractor(params.data)], true)
                                : '';
                        } else if (cd.cellfilter_expn) {
                            valueFormatter = (params) => utils.evaluate(cd.cellfilter_expn, this, false, null, false, [params.data, field_extractor(params.data)], true);
                        } else if (cd.condition_expn && !valueGetter) {
                            valueFormatter = (params) => utils.evaluate(cd.condition_expn, this, false, null, false, [params.data, field_extractor(params.data)], true)
                                ? field_extractor(params.data)
                                : '';
                        }

                        if (!valueGetter && !valueFormatter) {
                            valueFormatter = (params) => {
                                try {
                                    const data = field_extractor(params.data);
                                    return typeof data === 'object' ? JSON.stringify(data) : data;
                                } catch (e) {
                                    utils.warn(`Grid cell failed to evaluate`, e);
                                    return '';
                                }
                            };
                        }

                        if (valueGetter) colDef.valueGetter = valueGetter;
                        if (valueFormatter) colDef.valueFormatter = valueFormatter;

                        // Merged mode sorts in the browser: compare the raw field, as the server does.
                        if (this.merged && cd.Field) {
                            const raw = (data) => { try { return data ? field_extractor(data) : undefined; } catch (e) { return undefined; } };
                            colDef.comparator = (a, b, nodeA, nodeB, isDescending) => compareRaw(raw(nodeA && nodeA.data), raw(nodeB && nodeB.data), isDescending);
                        }
                    }

                    tempColumnDefs.push(colDef);
                }
            }
            this.columnDefs = markRaw(tempColumnDefs);
        },
        generateHeaders() {
            const headers = [];
            if (this.controlData.ColumnDefs) {
                for (let c of this.controlData.ColumnDefs) {
                    let cdef = {
                        field: c.Field,
                        text: this.Translate(c.DisplayName || c.Field),
                        align: c.Align || 'start',
                        sortable: !c.ActionOnlyColumn && c.Sortable !== false,
                        flex: 1,
                    };
                    headers.push(cdef);
                }
            }
            return headers;
        },
        ClearSelection() {
            this.gridApi.deselectAll();
            this.selected_items = [];
        },
        resizeColumns(e) {
            this.gridApi.autoSizeAllColumns();
        },
    },
    render() {
        if (!this.todisplay) return null;

        const style = {
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            padding: '8px',
            ...this.sizeStyle,
        };

        if (this.rowdata)
            this.rowdata.forEach((r) => {
                if (this.selectableexpn)
                    r.isSelectable = utils.evaluate(this.selectableexpn, this, false, null, false, r);
            });

        let resizebtn = (<QBtn flat icon="mdi-arrow-left-right-bold" dense size="md" onClick={(e) => this.resizeColumns(e)}>
        </QBtn>);

        resizebtn = utils.generateTooltip(h, resizebtn, this.Translate('Size columns to content'), 'bottom');

        return (
            <div
                v-show={this.isvisible}
                class={{ 'c-RealTimeGrid': true, [`c-name-${this.name || 'unnamed'}`]: true, 'ag-theme-alpine': true }}
                style={style}
            >
                <div class="gridHeaderButtons">
                    {this.controlData.EnableFilter ?
                        <QInput
                            class="q-mb-none quick-search-input"
                            dense
                            hide-bottom-space
                            clear-icon="mdi-close-circle"
                            clearable
                            placeholder={this.Translate("Quick Search")}
                            modelValue={this.searchText}
                            onUpdate:modelValue={(value) => this.updateSearchFilter(value)}
                        >
                            {{ prepend: () => <QIcon name="mdi-magnify" /> }}
                        </QInput> :
                        <div></div>
                    }
                    <QSpace />
                    {this.getGridHeaderControls()}
                    {resizebtn}
                    {this.getMenuContent()}
                </div>
                {this.mergeNotice ? <div class="text-caption q-px-sm">{this.mergeNotice}</div> : null}
                <AgGridVue
                    style={{ width: '100%', height: '100%' }}
                    class="ag-theme-alpine"
                    ref="agGridRef"
                    maintainColumnOrder={true}
                    gridOptions={this.gridOptions}
                    columnDefs={this.columnDefs}
                    rowSelection={this.agRowSelection}
                    rowModelType={this.merged ? 'clientSide' : 'serverSide'}
                    animateRows={true}
                    autoSizeStrategy={this.autoSizeStrategy}
                    serverSideDatasource={this.merged ? undefined : this.serverDataSource}
                    rowHeight={this.dense ? 28 : 44}
                    headerHeight={this.dense ? 32 : 48}

                    onGridReady={this.onGridReady}
                    onSortChanged={this.onSortChanged}
                    onFirstDataRendered={this.onFirstDataRendered}
                    onSelectionChanged={this.selectedItemsChanged}
                />
            </div>
        );
    }
});